// Logo (URL + upload + dual preview + invert + header scale), brand colors,
// and font. Controlled and stateless; the caller supplies its own <Section>.

import React from 'react';
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import LogoUploader from '../LogoUploader';
import type { UploadEndpoints } from '../uploadEndpoints';
import BrandingPreview from './BrandingPreview';
import { ColorField } from './FormSection';
import { inputClass, labelClass } from './publisherFieldStyles';
import type { BrandingValues } from './types';

// CTV-431: header-render-scale options surfaced in the admin UI. Backend
// accepts any number in [0.5, 3.0] but we keep the picker to a handful of
// presets so admins don't tune-by-pixel.
const LOGO_SCALE_OPTIONS: { value: number; label: string }[] = [
  { value: 1.0, label: '1×' },
  { value: 1.25, label: '1.25×' },
  { value: 1.5, label: '1.5×' },
  { value: 2.0, label: '2×' },
];

interface Props {
  values: BrandingValues;
  onChange: (patch: Partial<BrandingValues>) => void;
  /** True once a scrape returned a logo we can tell is opaque. */
  logoNotTransparent?: boolean;
  /** Re-pulls just the logo from the website URL; admin-only. */
  showExtractLogo?: boolean;
  extractingLogo?: boolean;
  onExtractLogo?: () => void;
  /**
   * The raw Logo URL input is an admin affordance (pasting a scraped or CDN
   * address). Publishers just upload a file, so the intake hides it — the
   * uploader still writes logoUrl behind the scenes.
   */
  showLogoUrlInput?: boolean;
  /**
   * The invert toggle + header scale are presentation knobs CTVBuyer staff
   * tune at finalize; the intake hides them and keeps the defaults.
   */
  showLogoAdjustments?: boolean;
  /** Defaults to the authenticated admin routes; the intake passes its own. */
  endpoints?: UploadEndpoints;
  /**
   * The secondary colour only feeds the creative-slot palette and changes
   * nothing a publisher can see while onboarding, so the publisher-facing
   * intake hides it rather than asking for a decision that does nothing.
   * CTVBuyer staff can still set it on the internal form.
   */
  showSecondaryColor?: boolean;
  /** Live wizard mock under the color pickers. */
  showPreview?: boolean;
  /** Name used in the preview's mock headline. */
  channelName?: string;
}

const BrandingFields: React.FC<Props> = ({
  values,
  onChange,
  logoNotTransparent = false,
  showExtractLogo = true,
  extractingLogo = false,
  onExtractLogo,
  showLogoUrlInput = true,
  showLogoAdjustments = true,
  endpoints,
  showSecondaryColor = true,
  showPreview = true,
  channelName,
}) => (
  <div className="space-y-5">
    <div className="space-y-3">
      {showLogoUrlInput && (
      <div>
        <label className={labelClass}>Logo URL</label>
        <div className="flex gap-2">
          <input
            required
            value={values.logoUrl}
            onChange={(e) => onChange({ logoUrl: e.target.value })}
            className={`${inputClass} flex-1`}
          />
          {showExtractLogo && (
            <button
              type="button"
              onClick={onExtractLogo}
              disabled={extractingLogo}
              title="Re-pull just the logo from the website URL above"
              className="inline-flex items-center gap-1.5 whitespace-nowrap px-4 py-2.5 rounded-xl text-[13px] font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 transition-colors"
            >
              {extractingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {extractingLogo ? 'Extracting…' : 'Extract logo'}
            </button>
          )}
        </div>
      </div>
      )}
      <LogoUploader onUploaded={(url) => onChange({ logoUrl: url })} endpoints={endpoints} />
      {values.logoUrl && (
        // Single on-light preview; the on-dark tile was dropped everywhere
        // (intake in CTV-680, internal form in CTV-745). The invert filter
        // mirrors how the portal header renders the logo, so with invert on
        // the preview washes out here by design.
        <div className="flex gap-2">
          <div className="flex-1 flex flex-col items-center gap-1.5 rounded-xl border p-3 border-slate-200 bg-slate-50">
            <img
              src={values.logoUrl}
              alt="logo preview"
              className={`max-h-16 object-contain ${values.logoInvert ? 'brightness-0 invert' : ''}`}
              style={{ transform: `scale(${values.logoScale})` }}
              onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
            />
            <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
              Logo preview
            </span>
          </div>
        </div>
      )}
      {logoNotTransparent && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            This scraped logo has a solid background, so it&rsquo;ll show as a box on the channel&rsquo;s
            portal. Ask the publisher for a transparent PNG/SVG and upload it above.
          </span>
        </p>
      )}
      {showLogoAdjustments && (
      <div className="flex flex-wrap items-center gap-4">
        <label className="inline-flex items-center gap-2 text-[12px] text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={values.logoInvert}
            onChange={(e) => onChange({ logoInvert: e.target.checked })}
            className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
          />
          Force logo to white (for dark headers)
        </label>
        <div className="flex items-center gap-2">
          <label className="text-[12px] text-slate-600">Header scale</label>
          <select
            value={String(values.logoScale)}
            onChange={(e) => onChange({ logoScale: Number(e.target.value) })}
            className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-[12px] text-slate-900 focus:outline-none focus:border-slate-900"
          >
            {LOGO_SCALE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>
      )}
    </div>
    <div className={`grid grid-cols-1 gap-3 ${showSecondaryColor ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
      <ColorField label="Primary" value={values.primaryColor} onChange={(v) => onChange({ primaryColor: v })} />
      {showSecondaryColor && (
        <ColorField label="Secondary" value={values.secondaryColor} onChange={(v) => onChange({ secondaryColor: v })} />
      )}
      <ColorField label="Accent" value={values.accentColor} onChange={(v) => onChange({ accentColor: v })} />
    </div>
    {showPreview && (
      <BrandingPreview
        values={values}
        channelName={channelName}
        showSecondaryNote={showSecondaryColor}
      />
    )}
  </div>
);

export default BrandingFields;
